import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createGlobalStyle, keyframes } from "styled-components";
import styled from "styled-components";
import SiteFooter from "../components/SiteFooter";

declare global { interface Window { IMP: any } }

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700;900&family=Pretendard:wght@400;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: #FFFBF0;
    font-family: 'Pretendard', 'Malgun Gothic', sans-serif;
    min-height: 100vh;
  }
`;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); }`;
const floatUp = keyframes`0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); }`;
const shimmer = keyframes`0% { opacity: 0.7; } 50% { opacity: 1; } 100% { opacity: 0.7; }`;

/* ── 면책 모달 ─────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(44, 24, 16, 0.72);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.3s ease;
`;
const ModalBox = styled.div`
  background: #FFFBF0;
  border-radius: 24px;
  max-width: 400px; width: 100%;
  padding: 36px 28px 28px;
  border: 2.5px solid #C8901E;
  box-shadow: 0 24px 60px rgba(44, 24, 16, 0.25);
  text-align: center;
  position: relative;
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 6px;
    background: linear-gradient(90deg, #CC3333, #C8901E, #4A7C6B, #C8901E, #CC3333);
    border-radius: 24px 24px 0 0;
  }
`;
const ModalIcon = styled.div`font-size: 2.4rem; margin-bottom: 12px; animation: ${shimmer} 2s infinite;`;
const ModalTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1.15rem; font-weight: 900;
  color: #2C1810; margin-bottom: 16px; line-height: 1.5;
`;
const ModalText = styled.div`
  font-size: 0.82rem; color: #5A3E2E; line-height: 1.9;
  margin-bottom: 24px;
  background: #FFF4E0;
  border-radius: 12px; padding: 14px 16px;
  border-left: 3px solid #C8901E;
  text-align: left;
`;
const ModalBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #C8901E, #E8A530);
  color: #fff; border: none; border-radius: 50px;
  padding: 14px; font-size: 0.95rem; font-weight: 800;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 20px rgba(200, 144, 30, 0.4);
  transition: transform 0.15s;
  &:active { transform: scale(0.97); }
`;

/* ── 전체 래퍼 ─────────────────────────────── */
const Wrap = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 0 40px;
  animation: ${fadeIn} 0.5s ease;
`;

/* ── 상단 단청 장식 바 ─────────────────────── */
const TopBar = styled.div`
  height: 8px;
  background: repeating-linear-gradient(
    90deg,
    #CC3333 0px, #CC3333 20px,
    #C8901E 20px, #C8901E 40px,
    #4A7C6B 40px, #4A7C6B 60px,
    #2E6DA4 60px, #2E6DA4 80px,
    #C8901E 80px, #C8901E 100px
  );
`;

/* ── 히어로 섹션 ────────────────────────────── */
const Hero = styled.div`
  background: linear-gradient(180deg, #FFF8E8 0%, #FFFBF0 100%);
  padding: 36px 20px 28px;
  text-align: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: '☁️';
    position: absolute; font-size: 5rem; opacity: 0.06;
    top: 10px; left: -10px;
  }
  &::after {
    content: '☁️';
    position: absolute; font-size: 4rem; opacity: 0.06;
    top: 20px; right: 0px;
  }
`;
const CharacterWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
`;
const CharacterImg = styled.img`
  width: 160px; height: 160px;
  object-fit: contain;
  border-radius: 50%;
  border: 4px solid #C8901E;
  box-shadow: 0 8px 32px rgba(200, 144, 30, 0.25), 0 0 0 8px rgba(200, 144, 30, 0.08);
  animation: ${floatUp} 3.5s ease-in-out infinite;
`;
const CharacterBadge = styled.div`
  position: absolute; bottom: 6px; right: -8px;
  background: #CC3333; color: #fff;
  font-size: 0.62rem; font-weight: 800;
  padding: 3px 9px; border-radius: 20px;
  box-shadow: 0 2px 8px rgba(204, 51, 51, 0.4);
  white-space: nowrap;
`;
const BrandName = styled.h1`
  font-family: 'Noto Serif KR', serif;
  font-size: 2rem; font-weight: 900;
  color: #2C1810; letter-spacing: -0.5px;
  margin-bottom: 6px;
`;
const BrandSub = styled.div`
  font-size: 0.8rem; color: #7A5C42;
  letter-spacing: 0.8px;
  margin-bottom: 14px;
`;
const RatingRow = styled.div`
  display: flex; justify-content: center; gap: 10px;
  flex-wrap: wrap;
`;
const RatingBadge = styled.div`
  display: flex; align-items: center; gap: 5px;
  background: #fff;
  border: 1.5px solid #E8D5B5;
  border-radius: 20px; padding: 5px 13px;
  font-size: 0.72rem; color: #5A3E2E; font-weight: 700;
  box-shadow: 0 2px 8px rgba(200, 144, 30, 0.1);
`;

/* ── 섹션 제목 ──────────────────────────────── */
const SectionTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1rem; font-weight: 900;
  color: #2C1810;
  padding: 24px 20px 12px;
  display: flex; align-items: center; gap: 8px;
  &::after {
    content: '';
    flex: 1; height: 1.5px;
    background: linear-gradient(90deg, #C8901E44, transparent);
  }
`;

/* ── 서비스 카드 — 씬 이미지 배경 + 누끼 캐릭터 ─── */
const CardWrap = styled.div`
  padding: 0 16px;
  display: flex; flex-direction: column; gap: 16px;
`;
const ServiceCard = styled.div`
  border-radius: 22px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(0,0,0,0.10);
  transition: transform 0.2s, box-shadow 0.2s;
  background: #fff;
  &:active { transform: scale(0.985); }
  &:hover { transform: translateY(-4px); box-shadow: 0 14px 40px rgba(0,0,0,0.16); }
`;
/* 씬 이미지 배경 영역 */
const CardBg = styled.div<{ $src: string }>`
  width: 100%; height: 200px;
  background-image: url(${p => p.$src});
  background-size: cover; background-position: center;
  position: relative;
  &::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 80px;
    background: linear-gradient(to bottom, transparent, #fff);
  }
`;
/* 누끼 캐릭터 — 배경 이미지 오른쪽 하단에 떠있음 */
const CardCharacter = styled.img`
  position: absolute; bottom: -10px; right: 12px; z-index: 2;
  width: 90px; height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.25));
  animation: ${floatUp} 3s ease-in-out infinite;
`;
/* 카드 하단 콘텐츠 */
const CardContent = styled.div<{ $color: string }>`
  padding: 6px 18px 18px;
  border-top: 3px solid ${p => p.$color}22;
`;
const CardLabel = styled.div<{ $color: string }>`
  font-size: 0.62rem; font-weight: 800; color: ${p => p.$color};
  letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 2px;
`;
const CardTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1.1rem; font-weight: 900; color: #2C1810; margin-bottom: 4px;
`;
const CardPrice = styled.div<{ $color: string }>`
  font-size: 0.72rem; color: ${p => p.$color}; font-weight: 700; margin-bottom: 8px;
`;
const CardDesc = styled.div`
  font-size: 0.8rem; color: #5A3E2E; line-height: 1.7; margin-bottom: 14px;
`;
const CardBtn = styled.div<{ $bg: string }>`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${p => p.$bg}; color: #fff; font-weight: 800;
  font-size: 0.82rem; padding: 10px 22px; border-radius: 50px;
  box-shadow: 0 4px 14px ${p => p.$bg}55;
`;

/* ── 아코디언 카드 ───────────────────────────── */
const AccCard = styled.div`
  margin: 0 16px 12px;
  background: #fff;
  border: 1.5px solid #E8D5B5;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(200, 144, 30, 0.08);
`;
const AccHeader = styled.button`
  width: 100%; background: none; border: none;
  padding: 20px; cursor: pointer;
  display: flex; align-items: center; gap: 12px;
  font-family: inherit;
`;
const AccArrow = styled.span<{ $open: boolean }>`
  color: #C8901E; font-size: 1rem;
  transition: transform 0.2s;
  transform: ${p => p.$open ? "rotate(90deg)" : "none"};
  margin-left: auto;
`;
const AccBody = styled.div`
  padding: 0 20px 20px;
  border-top: 1px solid #F0E8D8;
`;

/* ── 상품 ─────────────────────────────────────── */
const PHONE_SERVICES = [
  { key: "종합운세",  price: 50000 },
  { key: "신년운세",  price: 30000 },
  { key: "연애운",    price: 30000 },
  { key: "결혼운",    price: 30000 },
  { key: "직업운",    price: 30000 },
  { key: "사업운",    price: 30000 },
  { key: "건강운",    price: 30000 },
  { key: "연애궁합",  price: 50000 },
  { key: "심층궁합",  price: 80000 },
];

const NAVER_URL = "https://naver.me/xZjIiBpV";
const KAKAO_URL = "https://place.map.kakao.com/2033969175";
const PHONE_NUM = "010-4282-2377";
const IMP_CODE  = process.env.REACT_APP_IMP_CODE || "imp00000000";

export default function MainPage() {
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [openSection, setOpenSection] = useState<"phone"|"visit"|null>(null);
  const [selectedSvc, setSelectedSvc] = useState(PHONE_SERVICES[0].key);
  const [payResult, setPayResult] = useState("");

  useEffect(() => {
    if (!sessionStorage.getItem("woo_disclaimer_ok")) {
      setShowDisclaimer(true);
    }
  }, []);

  const closeDisclaimer = () => {
    sessionStorage.setItem("woo_disclaimer_ok", "1");
    setShowDisclaimer(false);
  };

  const toggle = (s: "phone"|"visit") => setOpenSection(p => p === s ? null : s);

  const handlePhonePayment = () => {
    const svc = PHONE_SERVICES.find(s => s.key === selectedSvc)!;
    const merchantUid = `phone_${selectedSvc}_${Date.now()}`;
    if (typeof window.IMP === "undefined") {
      setPayResult("❌ 결제 모듈 로딩 중입니다. 잠시 후 다시 시도하세요.");
      return;
    }
    window.IMP.init(IMP_CODE);
    window.IMP.request_pay({
      pg: "html5_inicis",
      pay_method: "card",
      merchant_uid: merchantUid,
      name: `우연 사주 전화상담 — ${svc.key}`,
      amount: svc.price,
      buyer_name: "", buyer_tel: "", buyer_email: "",
    }, (rsp: any) => {
      if (rsp.success) setPayResult(`✅ 결제 완료! ${PHONE_NUM}로 연락드리겠습니다.`);
      else setPayResult(`❌ 결제 실패: ${rsp.error_msg}`);
    });
  };

  return (
    <>
      <Global />

      {/* 면책 모달 */}
      {showDisclaimer && (
        <ModalOverlay onClick={closeDisclaimer}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalIcon>🐉</ModalIcon>
            <ModalTitle>우연 사주를 방문하신 것을<br />환영합니다</ModalTitle>
            <ModalText>
              우연사주는 정통 명리학 기반 사주와 약간의 개인적 해석으로
              다른 정통사주와는 다른 관점으로 풀이됨을 미리 알려드립니다.<br /><br />
              정통 사주학 관점의 풀이를 보시는 분들은 다른 곳으로의 방문을
              추천드립니다.
            </ModalText>
            <ModalBtn onClick={closeDisclaimer}>확인했습니다 →</ModalBtn>
          </ModalBox>
        </ModalOverlay>
      )}

      <TopBar />

      <Wrap>
        {/* 히어로 */}
        <Hero>
          <CharacterWrap>
            <CharacterImg src="/woo_y_1.png" alt="우연 사주 캐릭터" />
            <CharacterBadge>⭐ 후기 만점</CharacterBadge>
          </CharacterWrap>
          <BrandName>우연 사주</BrandName>
          <BrandSub>우연히 만난 내 인생의 지도</BrandSub>
          <RatingRow>
            <RatingBadge>🟢 네이버 예약</RatingBadge>
            <RatingBadge>💛 카카오 예약</RatingBadge>
            <RatingBadge>🐉 방문·전화·온라인</RatingBadge>
          </RatingRow>
        </Hero>

        {/* 서비스 카드 */}
        <SectionTitle>✨ 서비스 선택</SectionTitle>
        <CardWrap>

          {/* 쿠키사주 — woo_06 씬 배경 + woo_y_6 누끼 */}
          <ServiceCard onClick={() => navigate("/quick")}>
            <CardBg $src="/woo_06.png">
              <CardCharacter src="/woo_y_6.png" alt="우연" />
            </CardBg>
            <CardContent $color="#E8604C">
              <CardLabel $color="#E8604C">QUICK · 즉시 확인</CardLabel>
              <CardTitle>쿠키 사주</CardTitle>
              <CardDesc>
                궁금한 운세 하나만 딱 골라 즉시 확인하세요.<br />
                연애운 · 직장운 · 재물운 · 연애궁합<br />
                <strong style={{ color: "#2C1810" }}>결제 즉시 이메일로 결과 발송</strong>
              </CardDesc>
              <CardBtn $bg="#E8604C">바로 확인하기 →</CardBtn>
            </CardContent>
          </ServiceCard>

          {/* 온라인 사주 — woo_01 씬 배경 + woo_y_4 두루마리 누끼 */}
          <ServiceCard onClick={() => navigate("/client")}>
            <CardBg $src="/woo_01.png">
              <CardCharacter src="/woo_y_4.png" alt="두루마리" style={{ width: 80 }} />
            </CardBg>
            <CardContent $color="#4A7C6B">
              <CardLabel $color="#4A7C6B">ONLINE · 상세 리포트</CardLabel>
              <CardTitle>온라인 사주</CardTitle>
              <CardDesc>
                종합운세 · 신년운세 · 연애궁합 · 재회운 · 결혼운 등<br />
                8가지 운세 중 선택하여 심층 분석을 받으세요.<br />
                <strong style={{ color: "#2C1810" }}>결제 즉시 이메일로 결과 발송</strong>
              </CardDesc>
              <CardBtn $bg="#4A7C6B">운세 선택하기 →</CardBtn>
            </CardContent>
          </ServiceCard>

          {/* 방문 상담 — woo_02 씬 배경 + woo_y_2 누끼 */}
          <ServiceCard onClick={() => navigate("/visit")}>
            <CardBg $src="/woo_02.png">
              <CardCharacter src="/woo_y_2.png" alt="우연" />
            </CardBg>
            <CardContent $color="#C8901E">
              <CardLabel $color="#C8901E">VISIT · 1:1 대면 상담</CardLabel>
              <CardTitle>방문 상담 사주</CardTitle>
              <CardDesc>
                직접 방문하여 1:1 대면 심층 상담을 받으세요.<br />
                종합운세 · 연애궁합 · 결혼운 등 9가지 운세
              </CardDesc>
              <CardBtn $bg="#C8901E">방문 접수하기 →</CardBtn>
            </CardContent>
          </ServiceCard>
        </CardWrap>

        {/* 전화상담 */}
        <SectionTitle style={{ marginTop: 8 }}>📞 전화 상담</SectionTitle>
        <AccCard>
          <AccHeader onClick={() => toggle("phone")}>
            <span style={{ fontSize: "1.4rem" }}>📞</span>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontWeight: 900, color: "#2C1810", fontSize: "0.98rem" }}>전화 상담 예약</div>
              <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginTop: 2 }}>전화 통화로 1:1 맞춤 상담</div>
            </div>
            <AccArrow $open={openSection === "phone"}>▶</AccArrow>
          </AccHeader>
          {openSection === "phone" && (
            <AccBody>
              <a href={`tel:${PHONE_NUM.replace(/-/g,"")}`} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "linear-gradient(135deg,#2f9e44,#40c057)",
                color: "#fff", textDecoration: "none", borderRadius: 14,
                padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                marginBottom: 18, boxShadow: "0 4px 14px rgba(47,158,68,0.25)",
              }}>
                📞 &nbsp;{PHONE_NUM} 전화하기
              </a>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#7a6654", marginBottom: 10 }}>
                상담 예약 결제
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                {PHONE_SERVICES.map(s => (
                  <button key={s.key} onClick={() => setSelectedSvc(s.key)} style={{
                    padding: "10px 8px", borderRadius: 10, cursor: "pointer",
                    border: selectedSvc === s.key ? "2px solid #C8901E" : "1.5px solid #e0d5c8",
                    background: selectedSvc === s.key ? "#FFF8E8" : "#fff",
                    fontFamily: "inherit", textAlign: "left",
                  }}>
                    <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#2C1810" }}>{s.key}</div>
                    <div style={{ fontSize: "0.75rem", color: "#C8901E", marginTop: 2 }}>
                      {s.price.toLocaleString()}원
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={handlePhonePayment} style={{
                width: "100%", padding: "14px",
                background: "linear-gradient(135deg,#C8901E,#E8A530)",
                color: "#fff", border: "none", borderRadius: 14,
                fontSize: "0.95rem", fontWeight: 800, cursor: "pointer",
                boxShadow: "0 4px 14px rgba(200,144,30,0.35)", fontFamily: "inherit",
              }}>
                💳 &nbsp;{PHONE_SERVICES.find(s=>s.key===selectedSvc)?.price.toLocaleString()}원 결제하기
              </button>
              {payResult && (
                <div style={{
                  marginTop: 12, padding: "10px 14px",
                  background: payResult.startsWith("✅") ? "#ebfbee" : "#fff5f5",
                  borderRadius: 10, fontSize: "0.82rem", color: "#2C1810",
                  border: `1px solid ${payResult.startsWith("✅") ? "#8ce99a" : "#ffc9c9"}`,
                }}>{payResult}</div>
              )}
            </AccBody>
          )}
        </AccCard>

        {/* 방문 예약 */}
        <AccCard>
          <AccHeader onClick={() => toggle("visit")}>
            <span style={{ fontSize: "1.4rem" }}>📅</span>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontWeight: 900, color: "#2C1810", fontSize: "0.98rem" }}>방문 예약 (네이버·카카오)</div>
              <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginTop: 2 }}>날짜·시간 선택 후 방문</div>
            </div>
            <AccArrow $open={openSection === "visit"}>▶</AccArrow>
          </AccHeader>
          {openSection === "visit" && (
            <AccBody>
              <div style={{ fontSize: "0.78rem", color: "#9c7b5a", margin: "14px 0", lineHeight: 1.7 }}>
                원하는 날짜와 시간을 선택하여 예약하세요.<br />
                결제는 방문 당일 현장에서 진행됩니다.
              </div>
              <a href={NAVER_URL} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "#03C75A", color: "#fff", textDecoration: "none",
                borderRadius: 14, padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                marginBottom: 10, boxShadow: "0 4px 14px rgba(3,199,90,0.25)",
              }}>
                🟢 네이버에서 예약하기
              </a>
              <a href={KAKAO_URL} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "#FEE500", color: "#3C1E1E", textDecoration: "none",
                borderRadius: 14, padding: "13px", fontWeight: 800, fontSize: "0.95rem",
                boxShadow: "0 4px 14px rgba(254,229,0,0.35)",
              }}>
                💛 카카오에서 예약하기
              </a>
            </AccBody>
          )}
        </AccCard>

        <div style={{ padding: "0 16px" }}>
          <SiteFooter />
        </div>
      </Wrap>
    </>
  );
}
